
import { defineComponent, ref, onMounted, computed } from 'vue'
import { useStore } from '@/store'
import { useI18n } from 'vue-i18n'
import { leaseRateActions } from '@/store/leaseRates/actions'
import { LeaseRateItem } from '@/store/leaseRates/index'
import Loader from '@/components/UI/Loader.vue'
import Button from '@/components/UI/Button.vue'
import InputElement from '@/components/UI/Input.vue'
import ListingTable from '@/components/UI/ListingTable.vue'
import Popup from '@/components/Popup.vue'
import SelectionPopup from '@/components/UI/Popup.vue'
import Dropdown from '@/components/UI/Dropdown.vue'
import { useCompanyId } from '@/composables/useCompanyId'
import { CompanyActionTypes } from '@/store/company/actions'
import { companyLeaseRateMappingType, companyResponseType, LeaseRateTableHeading, ApiErrorResponse } from '@/types'

// ===== Constants & Types =====
const MAPPING_FIELDS = ['leaseRate', 'fuelAdvance', 'vgsEpCode', 'mileage', 'duration'] as const

// Supported manual mileage/duration values (must match the backend's allowed combinations)
const MILEAGE_OPTIONS: number[] = [20000, 25000, 30000, 35000, 40000, 45000]
const DURATION_OPTIONS: number[] = [36, 48, 60]

type MappingField = typeof MAPPING_FIELDS[number]

type LeaseRateErrorItem = {
  error: string;
  vehicleId?: string | number;
  [key: string]: unknown;
}

const EMPTY_MAPPING = (): companyLeaseRateMappingType => ({
  leaseRate: '',
  fuelAdvance: '',
  vgsEpCode: '',
  mileage: '',
  duration: ''
})

interface ParsedError {
  key: string;
  params: Record<string, string>;
}

interface ErrorPattern {
  regex: RegExp;
  key: string;
  extract: (match: RegExpMatchArray) => Record<string, string>;
}

// NOTE: These regex patterns parse backend error messages.
const ERROR_PATTERNS: ErrorPattern[] = [
  {
    regex: /Missing required columns: (.+)/,
    key: 'missingRequiredColumns',
    extract: (m) => ({ columns: m[1] })
  },
  {
    regex: /At Row Number (\d+) Unsupported mileage\/duration combination/,
    key: 'unsupportedMileageDurationCombination',
    extract: (m) => ({ rowId: m[1] })
  },
  {
    regex: /Row (\d+) has different values \(expected: (\d+)km\/(\d+)mo\)/,
    key: 'differentMileageDurationValues',
    extract: (m) => ({ rowId: m[1], expectedMileage: m[2], expectedDuration: m[3] })
  },
  {
    regex: /At Row Number (\d+) In (.+?) value is empty/,
    key: 'valueIsEmpty',
    extract: (m) => ({ rowId: m[1], key: m[2] })
  },
  {
    regex: /At Row Number (\d+) In (.+?) scientific notation is not allowed/,
    key: 'scientificNotationNotAllowed',
    extract: (m) => ({ rowId: m[1], key: m[2] })
  },
  {
    regex: /At Row Number (\d+) In (.+?) only positive integer values are allowed/,
    key: 'onlyPositiveIntegerAllowed',
    extract: (m) => ({ rowId: m[1], key: m[2] })
  },
  {
    regex: /At Row Number (\d+) In (.+?) only positive numeric values are allowed/,
    key: 'onlyPositiveNumericAllowed',
    extract: (m) => ({ rowId: m[1], key: m[2] })
  },
  {
    regex: /At Row Number (\d+) Invalid Vehicle Id found - must be at least (\d+) characters/,
    key: 'invalidVehicleIdMinLength',
    extract: (m) => ({ rowId: m[1], minLength: m[2] })
  },
  {
    regex: /At Row Number (\d+) Invalid Vehicle Id found - must be at most (\d+) characters/,
    key: 'invalidVehicleIdMaxLength',
    extract: (m) => ({ rowId: m[1], maxLength: m[2] })
  },
  {
    regex: /At Row Number (\d+) Invalid Vehicle Id found - must contain only digits/,
    key: 'invalidVehicleIdOnlyDigits',
    extract: (m) => ({ rowId: m[1] })
  },
  {
    regex: /At Row Number (\d+) Incorrect Lease Rate Value found/,
    key: 'incorrectLeaseRateValue',
    extract: (m) => ({ rowId: m[1] })
  },
  {
    regex: /At Row Number (\d+) In (.+?) non numeric value found/,
    key: 'nonNumericValueFound',
    extract: (m) => ({ rowId: m[1], key: m[2] })
  }
]

export default defineComponent({
  name: 'LeaseRates',
  components: { Loader, Button, InputElement, ListingTable, Popup, SelectionPopup, Dropdown },

  setup() {
    const store = useStore()
    const { t } = useI18n()
    const { compId } = useCompanyId()

    // State
    const isLoading = ref(false)
    const totalRecords = ref(0)
    const uploadSuccessKey = ref('')
    const mappingErrorKey = ref('')
    const mappingSuccessKey = ref('')
    const showDeletePopup = ref(false)
    const showUploadPopup = ref(false)
    const fileInput = ref<HTMLInputElement | null>(null)
    const selectedFile = ref<File | null>(null)
    // Optional per-upload manual mileage/duration. Kept local and never persisted to the mapping.
    const manualMileage = ref('')
    const manualDuration = ref('')
    // Validation message shown inside the upload popup (so it stays visible while open).
    const uploadPopupError = ref('')
    const showErrorPopup = ref(false)
    const showSuccessPopup = ref(false)
    const isPartialSuccess = ref(false)
    const carPolicyCombinations = ref<Array<{ mileage: number; duration: number; uploaded: boolean }>>([])
    const leaseRateToDelete = ref<LeaseRateItem | null>(null)
    const errorMessages = ref<ParsedError[]>([])

    const leaseRateMapping = ref(EMPTY_MAPPING())
    const originalMappings = ref(EMPTY_MAPPING())

    const mappingErrors = ref<Record<MappingField, string>>({
      leaseRate: '',
      fuelAdvance: '',
      vgsEpCode: '',
      mileage: '',
      duration: ''
    })

    const tableHeadings = ref<LeaseRateTableHeading[]>([
      { label: 'Filename', key: 'filename', sortable: false },
      { label: 'Contract duration', key: 'contractDuration', sortable: false },
      { label: 'Yearly mileage', key: 'yearlyMileage', sortable: false },
      { label: 'Uploaded by', key: 'createdBy', sortable: false },
      { label: 'Last update', key: 'lastUpdate', sortable: false }
    ])
    const tableData = ref<LeaseRateItem[]>([])

    // Computed
    const mappingsChanged = computed(() =>
      MAPPING_FIELDS.some((f) => leaseRateMapping.value[f] !== originalMappings.value[f])
    )

    const hasValidationErrors = computed(() => Object.values(mappingErrors.value).some((error) => error !== ''))

    // Helpers
    const clearMessages = (type: 'upload' | 'mapping' | 'all' = 'all') => {
      if (type === 'upload' || type === 'all') {
        uploadSuccessKey.value = ''
      }
      if (type === 'mapping' || type === 'all') {
        mappingErrorKey.value = ''
        mappingSuccessKey.value = ''
      }
    }

    // Transform lease rate data to extract createdBy name from object
    const transformLeaseRateData = (list: LeaseRateItem[]) => {
      return list.map(item => ({
        ...item,
        createdBy: typeof item.createdBy === 'object' && item.createdBy !== null
          ? (item.createdBy as { name?: string }).name || ''
          : item.createdBy || ''
      }))
    }

    const refreshTableData = () => {
      const { leaseRateList, totalRecords: total } = store.state.leaseRates
      tableData.value = transformLeaseRateData(leaseRateList)
      totalRecords.value = total
    }

    const parseError = (text: string): ParsedError => {
      for (const pattern of ERROR_PATTERNS) {
        const match = text.match(pattern.regex)
        if (match) {
          return { key: pattern.key, params: pattern.extract(match) }
        }
      }
      return { key: 'rawError', params: { message: text } }
    }

    const extractErrorMessage = (error: ApiErrorResponse): string | string[] => {
      // Check all possible error locations and provide a fallback
      if (error?.response?.data?.leaseRateResponse) return error.response.data.leaseRateResponse
      if (error?.response?.data?.message) return error.response.data.message
      if (error?.response?.data?.error) return error.response.data.error
      if (error?.message) return error.message

      // Default fallback if no error information is available
      return 'failedToProcessFile'
    }

    const showErrorPopupWith = (
      messages: string | Array<string | { error?: unknown }> | null | undefined
    ) => {
      const normalize = (msg: string | { error?: unknown }): string =>
        typeof msg === 'string'
          ? msg
          : String(msg?.error ?? 'Unknown error occurred')

      const list = !messages
        ? ['Unknown error occurred']
        : Array.isArray(messages)
          ? messages.map(normalize)
          : [messages]

      errorMessages.value = list.map(parseError)
      showErrorPopup.value = true
    }


    const formatErrorForDisplay = (error: ParsedError): string => {
      if (error.key === 'rawError') {
        // Try to translate the raw error message if it exists in translations
        const rawMessageKey = `_leaseRates.${error.params.message}`
        const translated = t(rawMessageKey)
        return translated !== rawMessageKey ? translated : error.params.message
      }

      const translationKey = `_leaseRates.${error.key}`
      const translated = t(translationKey, error.params)

      return translated !== translationKey ? translated : error.params.message || 'Unknown error'
    }

    // Validation
    const validateMappingField = (value: string): string => {
      const v = value.trim()
      if (!v) return ''

      // Allowed characters must match the backend sanitizeString set
      // (letters, numbers, space, and - _ . , ' " ( ) : ; [ ] { } ! ? @ & / + # %)
      // so nothing gets stripped on save.
      if (/[^A-Za-z0-9 \-_.,'"():;[\]{}!?@&/+#%]/.test(v)) {
        return t('_leaseRates.mappingFieldValidationError')
      }

      return ''
    }

    const validateFile = (file?: File): string | null => {
      if (!file) return 'pleaseSelectFileToUpload'
      if (!file.size) return 'uploadedFileIsEmpty'
      if (file.size > 10 * 1024 * 1024) return 'fileSizeExceedsMaximum'

      const nameParts = file.name.toLowerCase().split('.')
      const ext = nameParts.length > 1 ? `.${nameParts[nameParts.length - 1]}` : ''
      const validExts = ['.xlsx', '.xls']
      const validMimes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ]

      if (!validExts.includes(ext) && !validMimes.includes(file.type)) {
        return 'onlyXlsxFilesAccepted'
      }
      return null
    }

    const withLoading = async (fn: () => Promise<void>) => {
      try {
        isLoading.value = true
        await fn()
      } finally {
        isLoading.value = false
      }
    }

    const normalizeResponse = (res: Record<string, unknown>) => res?.data ?? res

    // Fetch car policy combinations from company data
    const fetchCombinations = async () => {
      await store.dispatch(CompanyActionTypes.GET_COMPANYBYID, { companyId: Number(compId.value) })
      const company = store.state.company.getCompanyById as companyResponseType
      carPolicyCombinations.value = company?.carPolicyCombinations || []
    }

    // Initialization
    const initialize = () =>
      withLoading(async () => {
        await Promise.all([
          store.dispatch(leaseRateActions.GET_LEASERATE_MAPPINGS, Number(compId.value)),
          store.dispatch(leaseRateActions.GET_ALL_LEASERATES, Number(compId.value)),
          fetchCombinations()
        ])

        const { leaseRateList, totalRecords: totalCount, leaseRateMappings } = store.state.leaseRates

        tableData.value = transformLeaseRateData(leaseRateList)
        totalRecords.value = totalCount

        if (leaseRateMappings) {
          leaseRateMapping.value = { ...leaseRateMappings }
          originalMappings.value = { ...leaseRateMappings }
        }
      })

    // File Upload
    // Per-upload manual values are local only; reset them after each use so they are never persisted.
    const resetManualValues = () => {
      manualMileage.value = ''
      manualDuration.value = ''
    }

    const handleFileChange = (event: Event) => {
      const input = event.target as HTMLInputElement
      const file = input.files?.[0]

      // Validate companyId before proceeding
      if (!compId.value) {
        uploadPopupError.value = 'companyIdNotFound'
        input.value = ''
        return
      }

      const validationError = validateFile(file)
      if (validationError) {
        uploadPopupError.value = validationError
        input.value = ''
        return
      }

      uploadPopupError.value = ''
      selectedFile.value = file ?? null
    }

    const startUpload = async () => {
      const file = selectedFile.value
      if (!file) return

      clearMessages('upload')
      // Close the popup immediately; the page's existing loading state takes over.
      showUploadPopup.value = false

      try {
        isLoading.value = true

        const formData = new FormData()
        formData.append('file', file)

        // Only send manual mileage/duration when both are set; the backend then
        // ignores the mapped columns for this upload. Values are never persisted.
        if (manualMileage.value && manualDuration.value) {
          formData.append('mileage', manualMileage.value)
          formData.append('duration', manualDuration.value)
        }

        const response = await store.dispatch(leaseRateActions.UPLOAD_LEASERATES, {
          companyId: Number(compId.value),
          body: formData
        })
        const data = response?.data || response
        const leaseRateResponse = data?.leaseRateResponse

        // Extract error items from response (handles both array and object formats)
        let errorItems: LeaseRateErrorItem[] = []
        if (leaseRateResponse) {
          if (Array.isArray(leaseRateResponse)) {
            errorItems = leaseRateResponse.filter(item => item?.error)
          } else if (typeof leaseRateResponse === 'object') {
            // Handle object format with numeric keys: { "0": { error: "..." }, "1": { error: "..." }, ... }
            errorItems = Object.keys(leaseRateResponse)
              .filter(key => /^\d+$/.test(key))
              .map(key => leaseRateResponse[key])
              .filter(item => item?.error)
          }
        }

        // Show error popup based on status and error items
        if (data?.status === 'success' && errorItems.length > 0) {
          // Upload succeeded but some rows were skipped due to validation errors
          isPartialSuccess.value = true
          showErrorPopupWith(errorItems)
        } else if (data?.status === 'error') {
          // Full upload failure
          isPartialSuccess.value = false
          if (errorItems.length > 0) {
            showErrorPopupWith(errorItems)
          } else {
            // Handle string format: {"status":"error","leaseRateResponse":"Missing required columns: ..."}
            const errorMessage = typeof leaseRateResponse === 'string'
              ? leaseRateResponse
              : (data?.message || 'failedToProcessFile')
            showErrorPopupWith(errorMessage)
          }
        }

        // On success, refresh data
        if (data?.status === 'success') {
          await initialize()
          uploadSuccessKey.value = 'fileUploadedSuccessfully'
        } else {
          await initialize()
        }
      } catch (error) {
        showErrorPopupWith(extractErrorMessage(error as ApiErrorResponse))
      } finally {
        isLoading.value = false
        resetManualValues()
        selectedFile.value = null
        uploadPopupError.value = ''
        if (fileInput.value) fileInput.value.value = ''
      }
    }

    const openUploadPopup = () => {
      clearMessages('upload')
      resetManualValues()
      selectedFile.value = null
      uploadPopupError.value = ''
      showUploadPopup.value = true
    }

    const closeUploadPopup = () => {
      showUploadPopup.value = false
      resetManualValues()
      selectedFile.value = null
      uploadPopupError.value = ''
      if (fileInput.value) fileInput.value.value = ''
    }

    const triggerFileSelect = () => {
      // Manual mileage and duration must be set together or not at all.
      if (!manualMileage.value !== !manualDuration.value) {
        uploadPopupError.value = 'manualMileageDurationBothRequired'
        return
      }
      uploadPopupError.value = ''
      fileInput.value?.click()
    }

    const onUploadPopupButtonClick = () => {
      selectedFile.value ? startUpload() : triggerFileSelect()
    }

    const onMileageChange = (value: number) => {
      manualMileage.value = String(value)
      uploadPopupError.value = ''
    }

    const onDurationChange = (value: number) => {
      manualDuration.value = String(value)
      uploadPopupError.value = ''
    }

    // Mappings
    const saveMappings = () =>
      withLoading(async () => {
        clearMessages('mapping')

        let hasErrors = false
        MAPPING_FIELDS.forEach((field) => {
          const error = validateMappingField(leaseRateMapping.value[field])
          mappingErrors.value[field] = error
          if (error) hasErrors = true
        })

        if (hasErrors) {
          mappingErrorKey.value = t('_leaseRates.fixValidationErrors')
          return
        }

        const body = Object.fromEntries(
          MAPPING_FIELDS.map((f) => [f, leaseRateMapping.value[f].trim()])
        ) as companyLeaseRateMappingType

        const response = normalizeResponse(
          await store.dispatch(leaseRateActions.UPDATE_LEASERATE_MAPPINGS, {
            companyId: Number(compId.value),
            body
          })
        ) as { status?: string; message?: string }

        if (response?.status === 'error') {
          mappingErrorKey.value = response.message || 'failedToSaveMappings'
          return
        }

        originalMappings.value = { ...leaseRateMapping.value }
        mappingSuccessKey.value = response.message || 'mappingsSavedSuccessfully'
        await store.dispatch(leaseRateActions.GET_ALL_LEASERATES, Number(compId.value))
        refreshTableData()
      })

    const updateMappingField = (field: MappingField, value: string) => {
      clearMessages('mapping')
      leaseRateMapping.value[field] = value
      mappingErrors.value[field] = validateMappingField(value)
    }

    // Delete
    const handleDeleteLeaseRate = (item: LeaseRateItem) => {
      leaseRateToDelete.value = item
      showDeletePopup.value = true
    }

    const confirmDelete = () => {
      if (!leaseRateToDelete.value?.id) {
        console.error('Cannot delete: Invalid lease rate ID')
        showDeletePopup.value = false
        return
      }

      return withLoading(async () => {
        showDeletePopup.value = false

        const response = normalizeResponse(
          await store.dispatch(leaseRateActions.DELETE_LEASERATE, {
            companyId: Number(compId.value),
            leaseRateId: leaseRateToDelete.value?.id
          })
        ) as { status?: string; message?: string }

        if (response?.status === 'error') {
          showErrorPopupWith(response.message || 'failedToDeleteLeaseRate')
          return
        }

        await store.dispatch(leaseRateActions.GET_ALL_LEASERATES, Number(compId.value))
        await fetchCombinations()
        refreshTableData()
        leaseRateToDelete.value = null
      })
    }

    const cancelDelete = () => {
      showDeletePopup.value = false
      leaseRateToDelete.value = null
    }

    const closeErrorPopup = () => {
      showErrorPopup.value = false
      errorMessages.value = []
      isPartialSuccess.value = false
    }

    const closeSuccessPopup = () => {
      showSuccessPopup.value = false
    }

    onMounted(initialize)

    return {
      isLoading,
      uploadSuccessKey,
      mappingErrorKey,
      mappingSuccessKey,
      totalRecords,
      leaseRateMapping,
      mappingErrors,
      mappingsChanged,
      hasValidationErrors,
      showDeletePopup,
      showUploadPopup,
      fileInput,
      selectedFile,
      manualMileage,
      manualDuration,
      uploadPopupError,
      mileageOptions: MILEAGE_OPTIONS,
      durationOptions: DURATION_OPTIONS,
      showErrorPopup,
      showSuccessPopup,
      isPartialSuccess,
      carPolicyCombinations,
      errorMessages,
      leaseRateToDelete,
      tableHeadings,
      tableData,
      handleFileChange,
      startUpload,
      openUploadPopup,
      closeUploadPopup,
      triggerFileSelect,
      onUploadPopupButtonClick,
      onMileageChange,
      onDurationChange,
      saveMappings,
      updateMappingField,
      handleDeleteLeaseRate,
      confirmDelete,
      cancelDelete,
      closeErrorPopup,
      closeSuccessPopup,
      formatErrorForDisplay
    }
  }
})
